import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setOfflineStatus } from '../store/slices/uiSlice';
import type { RootState } from '../store';

/**
 * Custom hook for detecting and managing offline status
 * 
 * Monitors browser online/offline events and updates Redux state accordingly.
 * Provides offline status and sync-related information to components.
 * 
 * @returns {Object} Offline state and utilities
 * @returns {boolean} isOffline - Current offline status
 * @returns {boolean} isSyncing - Whether background sync is in progress
 * @returns {number} pendingActionsCount - Number of queued offline actions
 */
export const useOffline = () => {
  const dispatch = useDispatch();
  const isOffline = useSelector((state: RootState) => state.ui.isOffline);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);

  useEffect(() => {
    // Initialize offline status based on navigator.onLine
    const updateOnlineStatus = () => {
      const offline = !navigator.onLine;
      dispatch(setOfflineStatus(offline));
    };

    // Set initial status
    updateOnlineStatus();

    // Event handlers for online/offline events
    const handleOnline = () => {
      dispatch(setOfflineStatus(false));
      // Trigger background sync when coming back online
      if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
        navigator.serviceWorker.ready
          .then((registration) => {
            return registration.sync.register('sync-offline-actions');
          })
          .catch((error) => {
            console.error('Background sync registration failed:', error);
          });
      }
    };

    const handleOffline = () => {
      dispatch(setOfflineStatus(true));
    };

    // Add event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for sync status updates from service worker
    const handleSyncMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'SYNC_STATUS') {
        setIsSyncing(event.data.syncing);
        if (event.data.pendingCount !== undefined) {
          setPendingActionsCount(event.data.pendingCount);
        }
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSyncMessage);
    }

    // Check for pending actions count on mount
    const checkPendingActions = async () => {
      try {
        // Import dynamically to avoid circular dependencies
        const { offlineStorage } = await import('../services/offlineStorage');
        const count = await offlineStorage.pendingActions.count();
        setPendingActionsCount(count);
      } catch (error) {
        console.error('Failed to check pending actions:', error);
      }
    };

    checkPendingActions();

    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSyncMessage);
      }
    };
  }, [dispatch]);

  return {
    isOffline,
    isSyncing,
    pendingActionsCount,
  };
};

export default useOffline;